import { isPartialDate, nowPartialDate } from 'neuro-utils';
import { TPlatformHandler } from '.';
import { partialDateToString } from '../utils/queryFormatters';
import { maxDateToNextYearOrMonth } from '../utils';

export const handleEpilepsyField: TPlatformHandler = (field, state) => {
  let query: string | undefined = undefined;

  switch (field) {
    case 'seizureClassification': {
      query = '';
      if (state.seizureClassification === 'noCropping') break;
      const criteria = state.seizureClassification.filter((c): c is NonNullable<typeof c> => !!c);
      if (criteria.length === 0) break;

      // AND between classification criteria: each criterion must match a separate seizureType document
      const parts = criteria.map(c => `seizureType@_€{data.classification.classification==$${c}}`);
      query += parts.join('&');

      break;
    }

    case 'syndrome': {
      query = '';
      if (state.syndrome === 'noCropping') break;
      const values = state.syndrome.filter((s): s is NonNullable<typeof s> => s !== null);
      if (values.length === 0) break;

      query += 'syndrome@_€{';

      // OR between syndrome criteria
      const parts = values.map(s => `data.syndrome==$${s}`);
      query += parts.length > 1 ? `${parts.join('|')}` : parts[0];
      query += '}';

      break;
    }

    case 'etiology': {
      query = '';
      if (state.etiology === 'noCropping') break;
      const criteria = state.etiology.filter(c => c.class !== null);
      if (criteria.length === 0) break;

      query += 'etiology@_€{';

      // OR between etiology criteria
      const parts = criteria.map(c => {
        let part = `data.class==$${c.class}`;
        if (c.subClass) {
          // subClass maps to the corresponding subClass field based on class
          const subClassField = {
            structural: 'subClassStructural',
            genetic: 'subClassGenetic',
            infectional: 'subClassInfectional',
            immune: 'subClassImmune',
          }[c.class as string];
          // metabolism / unknown have no sub-classes, but subClass might be set to specificClass
          const innerField = subClassField ?? 'specificClass';
          part += `&data.subClasses.*€{data.${innerField}==$${c.subClass}}`;
        }
        return part;
      });

      query += parts.length > 1 ? `${parts.join('|')}` : parts[0];
      query += '}';

      break;
    }

    case 'vnsTreatment': {
      query = '';
      if (state.vnsTreatment === 'noCropping') break;
      const { status, leadType, generator } = state.vnsTreatment;

      const buildVnsStatusPart = (s: typeof status): string | undefined => {
        if (!s || s.length === 0) return undefined;
        const parts: string[] = [];
        if (s.includes('ongoing')) parts.push('data.hasEnded€{exists==FALSE|data.0==FALSE|data.length==#0}');
        if (s.includes('ended')) parts.push('(data.hasEnded€{exists==TRUE&data.0==TRUE})');
        return parts.length > 1 ? `(${parts.join('|')})` : parts[0];
      };

      if (leadType || generator) {
        const parts: string[] = [];
        const sp = buildVnsStatusPart(status);
        if (sp) parts.push(sp);
        if (leadType) parts.push(`data.leads.0.leadType==$${leadType}`);
        if (generator) parts.push(`data.generators.0.generator==$${generator}`);
        query = `§getLatestDocumentFieldValue[$vns,$_,$date]€{${parts.join('&')}}`;
      } else {
        const sp = buildVnsStatusPart(status);
        if (sp) query = `vns@_€{${sp}}`;
      }
      break;
    }

    case 'epilepsyDbsTreatment': {
      query = '';
      if (state.epilepsyDbsTreatment === 'noCropping') break;
      const { status, lead, generator } = state.epilepsyDbsTreatment;

      const buildDbsStatusPart = (s: typeof status): string | undefined => {
        if (!s || s.length === 0) return undefined;
        const parts: string[] = [];
        if (s.includes('ongoing')) parts.push('data.hasEnded€{exists==FALSE|data.0==FALSE|data.length==#0}');
        if (s.includes('ended')) parts.push('(data.hasEnded€{exists==TRUE&data.0==TRUE})');
        return parts.length > 1 ? `(${parts.join('|')})` : parts[0];
      };

      if (lead || generator) {
        const parts: string[] = [];
        const sp = buildDbsStatusPart(status);
        if (sp) parts.push(sp);
        if (lead) parts.push(`(data.leadRight==$${lead}|data.leadLeft==$${lead})`);
        if (generator) parts.push(`data.generators.0.generator==$${generator}`);
        query = `§getLatestDocumentFieldValue[$dbs,$_,$date]€{${parts.join('&')}}`;
      } else {
        const sp = buildDbsStatusPart(status);
        if (sp) query = `dbs@_€{${sp}}`;
      }
      break;
    }

    case 'surgicalTreatment': {
      query = '';
      if (state.surgicalTreatment === 'noCropping') break;
      const { status, operationType, dateFrom, dateTo, engelClassificationAtOneYear } = state.surgicalTreatment;

      if (!status) break;

      if (status === 'done') {
        const doneParts: string[] = ['data.date!=NULL'];

        if (operationType && operationType.length > 0) {
          const typeList = operationType.map(t => `$${t}`).join(',');
          doneParts.push(`data.operations.*€{data.typeOfOperation€$[${typeList}]}`);
        }

        if (isPartialDate(dateFrom)) {
          doneParts.push(`data.date>=%${partialDateToString(dateFrom)}`);
        }

        if (isPartialDate(dateTo)) {
          doneParts.push(`data.date<=%${partialDateToString(maxDateToNextYearOrMonth(dateTo))}`);
        }

        if (engelClassificationAtOneYear) {
          doneParts.push(`data.engelClassification.*€{data.timeFromOperation==$1}`);
        }

        query = `surgicalTreatment@_€{${doneParts.join('&')}}`;
      } else {
        // notDone
        query = '!surgicalTreatment@date!=NULL';
      }
      break;
    }

    case 'seizureFrequency': {
      query = '';
      if (state.seizureFrequency === 'noCropping') break;
      const { classification, seizures, periods } = state.seizureFrequency;

      if (!seizures || !periods) break;

      const { minSeizures, maxSeizures } = seizures;
      if (minSeizures === undefined && maxSeizures === undefined) break;

      const endDate = nowPartialDate();
      const formatDateArg = (p: PartialDate): string => `%"${partialDateToString(p)}"`;

      let seizureTypeArgs = '';
      if (classification && classification.length > 0) {
        seizureTypeArgs = ',' + classification.map(c => `$${c}`).join(',');
      }

      const filterParts: string[] = [];
      if (maxSeizures === 0) {
        filterParts.push('total_in_timeframe==#0');
      } else {
        if (minSeizures !== undefined && minSeizures > 0) {
          filterParts.push(`min_count_in_period>=#${minSeizures}`);
        }
        if (maxSeizures !== undefined) {
          filterParts.push(`max_count_in_period<=#${maxSeizures}`);
        }
      }

      if (filterParts.length === 0) break;

      query = `§getSeizureFrequencySummary[${formatDateArg(endDate)},#28,#${periods}${seizureTypeArgs}]@_€{${filterParts.join('&')}}`;
      break;
    }

    case 'dietTherapy': {
      query = '';
      if (state.dietTherapy === 'noCropping') break;
      if (!Array.isArray(state.dietTherapy) || state.dietTherapy.length === 0) break;

      query += 'diet@_€{';

      const parts: string[] = [];
      if (state.dietTherapy.includes('ongoing')) {
        parts.push('data.startDate!=NULL&data.endDate€{exists==FALSE|data==NULL}');
      }
      if (state.dietTherapy.includes('ended')) {
        parts.push('data.endDate€{exists==TRUE&data!=NULL}');
      }

      query += parts.length > 1 ? `${parts.join('|')}` : parts[0];
      query += '}';

      break;
    }

    default:
      return undefined;
  }

  return query;
};
